<script setup>
import { ref, onMounted, watch, computed } from 'vue'

import layoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'
import AppDialog from '@/components/ui/AppDialog.vue'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import Checkbox from 'primevue/checkbox'
import Message from 'primevue/message'

import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { getUsers, createUser, updateUser, deleteUser } from '@/services/users'
import api from '@/services/axios'
import apiClient from '@/api'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const notify = useNotify()
const userStore = useUserStore()
const { t } = useI18n()
const { confirmDelete } = useConfirmDelete()

/* ---- Capabilities ----
 * Globaler Admin: admin.*  |  Company-Admin: company_users.* (mandanten-begrenzt).
 * Bearbeiten bestehender User bleibt global-Admin-only (Backend erzwingt das).
 */
const isGlobalAdmin = computed(() => userStore.isAdmin)
const canView   = computed(() => userStore.can('admin.view')   || userStore.can('company_users.view'))
const canCreate = computed(() => userStore.can('admin.create') || userStore.can('company_users.invite'))
const canDelete = computed(() => userStore.can('admin.delete') || userStore.can('company_users.delete'))
const canEdit   = computed(() => userStore.can('admin.change'))

// Rollen, die der aktuelle User vergeben darf. Company-Admins: nur User/Company
// Admin (feste Liste, da /roles admin.view erfordert und für sie 403 liefert —
// muss mit COMPANY_ADMIN_ASSIGNABLE_ROLES im Backend übereinstimmen).
const COMPANY_ADMIN_ROLES = ['User', 'Company Admin']
const assignableRoleNames = computed(() => {
  if (isGlobalAdmin.value) return roleNames.value.length ? roleNames.value : DEFAULT_ROLES
  return COMPANY_ADMIN_ROLES
})

// Effektive RBAC-Rollen einer User-Zeile. roles[] kommt aus user_roles; die
// Legacy-Spalte row.role (nur User/Admin) ist Fallback für Altdaten.
function effectiveRoles(row) {
  return Array.isArray(row?.roles) && row.roles.length
    ? row.roles
    : (row?.role ? [row.role] : [])
}
// Primäre Rolle für die Single-Select-Bearbeitung: bevorzugt die erste Nicht-User-Rolle.
function primaryRole(row) {
  const rs = effectiveRoles(row)
  if (!rs.length) return 'User'
  return rs.find(r => String(r).toLowerCase() !== 'user') || rs[0]
}
function roleSeverity(name) {
  return String(name).toLowerCase() === 'admin' ? 'danger' : 'info'
}

/* ---- Table State ---- */
const loading = ref(false)
const users   = ref([])
const total   = ref(0)

const sortField = ref('created_at')
const sortOrder = ref('desc')
const roleFilter   = ref(null)
const activeFilter = ref(null)

/* ---- Dialog State ---- */
const showDialog = ref(false)
const isEdit     = ref(false)
// 'invite' = Mail mit Aktivierungs-Link, 'manual' = Admin gibt Passwort vor
const createMode = ref('invite')
const createModeOptions = computed(() => [
  { label: t('admin.users.modeInvite'), value: 'invite', icon: 'pi pi-envelope' },
  { label: t('admin.users.modeManual'), value: 'manual', icon: 'pi pi-key' },
])
const form       = ref({
  id: null, password: '', first_name: '', last_name: '',
  email: '', avatar_url: '', role: 'User', is_active: true, company_id: null
})

/* ---- Companies (für Dropdown) ---- */
const companies = ref([])
const companyOptions = computed(() => [
  { label: t('admin.users.noCompany'), value: null },
  ...companies.value.map(c => ({ label: c.name, value: c.id })),
])
async function loadCompanies() {
  try {
    const { data } = await apiClient.getCompanies()
    companies.value = Array.isArray(data) ? data : (data?.companies ?? [])
  } catch { /* non-critical */ }
}

const columns = [
  { field: 'first_name',   header: t('admin.users.colName'),    sortable: true,  default: true  },
  { field: 'email',        header: t('admin.users.colEmail'),   sortable: true,  default: true  },
  { field: 'company_name', header: t('admin.users.colCompany'), sortable: false, default: true  },
  { field: 'role',         header: t('admin.users.colRole'),    sortable: true,  default: true  },
  { field: 'is_active',    header: t('admin.users.colActive'),  sortable: true,  default: true  },
  { field: 'created_at',   header: t('admin.users.colCreated'), sortable: true,  default: false },
]

/* ---- Rollen (dynamisch) ---- */
const roleNames = ref([])
const DEFAULT_ROLES = ['User', 'Admin']
const roleChoiceOptions = computed(() =>
  assignableRoleNames.value.map(n => ({ label: n, value: n }))
)

async function loadRoleOptions() {
  try {
    const { data } = await api.get('/roles', { params: {} })
    const raw = Array.isArray(data?.roles) ? data.roles : Array.isArray(data) ? data : []
    roleNames.value = Array.from(new Set(raw.map(r => r?.name ?? r?.role ?? '').filter(Boolean))).sort((a, b) => a.localeCompare(b))
  } catch {
    roleNames.value = DEFAULT_ROLES
  }
}

/* ---- Load (Server-seitig) ---- */
async function load(q = {}) {
  if (!canView.value) return
  loading.value = true
  try {
    const params = {
      page:    q.page  ?? 1,
      limit:   q.limit ?? 10,
      q:       q.search?.trim() || undefined,
      role:    roleFilter.value || undefined,
      active:  activeFilter.value ?? undefined,
      sortBy:  q.sortField || sortField.value,
      sortDir: q.sortOrder || sortOrder.value,
    }
    const { data } = await getUsers(params)
    users.value = data?.users ?? data?.rows ?? []
    total.value = data?.total ?? users.value.length
  } catch (e) {
    notify.error(t('common.error'), t('admin.users.loadFailed'))
  } finally {
    loading.value = false
  }
}

watch([roleFilter, activeFilter], () => load({ page: 1 }))

function onQueryChange(q) { load(q) }

/* ---- Dialog Actions ---- */
function openCreate() {
  if (!canCreate.value) return
  isEdit.value = false
  createMode.value = 'invite'
  showDialog.value = true
  const defaultRole = assignableRoleNames.value[0] || 'User'
  form.value = { id: null, password: '', first_name: '', last_name: '', email: '', avatar_url: '', role: defaultRole, is_active: true, company_id: null }
}

function openEdit(row) {
  if (!canEdit.value) return
  isEdit.value = true
  showDialog.value = true
  form.value = {
    id: row.id, password: '',
    first_name: row.first_name || '', last_name: row.last_name || '',
    email: row.email || '', avatar_url: row.avatar_url || '',
    role: primaryRole(row), is_active: row.is_active !== false,
    company_id: row.company_id ?? null,
  }
}

function closeDialog() { showDialog.value = false }

function validateForm() {
  if (!(form.value.first_name || '').trim()) return t('admin.users.validationFirstName')
  if (!(form.value.last_name  || '').trim()) return t('admin.users.validationLastName')
  if (!(form.value.email      || '').trim()) return t('admin.users.validationEmail')
  if (!(form.value.role       || '').trim()) return t('admin.users.validationRole')
  // Passwort-Pflicht nur beim manuellen Create-Mode oder wenn der Edit-Dialog
  // aktiv ein Passwort befüllt hat (leer = unverändert lassen).
  if (!isEdit.value && createMode.value === 'manual'
      && (!form.value.password || form.value.password.length < 8)) {
    return t('admin.users.validationPassword')
  }
  return null
}

async function save() {
  if (isEdit.value && !canEdit.value) return
  if (!isEdit.value && !canCreate.value) return
  const v = validateForm()
  if (v) { notify.warn(t('common.validation'), v); return }
  try {
    const payload = {
      first_name: (form.value.first_name || '').trim(),
      last_name:  (form.value.last_name  || '').trim(),
      email:      (form.value.email      || '').trim(),
      role:       (form.value.role       || 'User').trim(),
      is_active:  !!form.value.is_active,
    }
    // company_id darf nur der globale Admin frei setzen; bei Company-Admins
    // erzwingt das Backend ohnehin die eigene Company.
    if (isGlobalAdmin.value) payload.company_id = form.value.company_id ?? null
    if (form.value.avatar_url?.trim().length) payload.avatar_url = form.value.avatar_url.trim()

    if (isEdit.value && form.value.id) {
      // Edit: Passwort nur mitsenden wenn neu gesetzt
      if (form.value.password?.length >= 8) payload.password = form.value.password
      await updateUser(form.value.id, payload)
      notify.success(t('admin.users.saved'), t('admin.users.userUpdated'))
    } else {
      // Create: je nach Mode entweder Passwort oder Invite-Flag
      if (createMode.value === 'invite') {
        payload.send_invite = true
      } else {
        payload.password = form.value.password
      }
      await createUser(payload)
      notify.success(
        t('admin.users.created'),
        createMode.value === 'invite'
          ? t('admin.users.userCreatedInvite').replace('{email}', payload.email)
          : t('admin.users.userCreated')
      )
    }
    showDialog.value = false
    await load()
  } catch (e) {
    notify.error(t('common.error'), e?.response?.data?.message || e?.message || t('admin.users.saveFailed'))
  }
}

async function removeUsers(rows) {
  const emails = rows.map(r => r.email).join(', ')
  if (!await confirmDelete(t('admin.users.confirmDelete').replace('{email}', emails))) return
  try {
    await Promise.all(rows.map(r => deleteUser(r.id)))
    notify.success(t('admin.users.deleted'), t('admin.users.userDeleted'))
    await load()
  } catch (e) {
    notify.error(t('common.error'), e?.response?.data?.message || t('admin.users.deleteFailed'))
  }
}

const tableActions = computed(() => [
  canEdit.value && {
    label:          t('admin.users.editTooltip'),
    icon:           'pi pi-file-edit',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  canDelete.value && {
    label:     t('admin.users.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   removeUsers,
  },
].filter(Boolean))

onMounted(async () => {
  await Promise.all([loadRoleOptions(), loadCompanies()])
  if (!userStore.info && !userStore.isLoading) {
    try { await userStore.fetchMe(api) } catch { /* ignore */ }
  }
  if (canView.value) await load()
})

watch(() => userStore.info, async (val) => {
  if (val && canView.value) await load()
})
</script>

<template>
  <layoutShell>
    <div class="layout-content-wrapper">
      <main class="content">
        <div v-if="userStore.isLoading && !userStore.info" class="p-4">
          <h3>{{ t('admin.loadingUser') }}</h3>
        </div>

        <div v-else-if="!canView" class="p-4">
          <h2>{{ t('admin.noPermission') }}</h2>
          <p>{{ t('admin.noPermissionHint').replace('{perm}', 'company_users.view') }}</p>
        </div>

        <template v-else>
          <AppTable
            table-key="admin-users"
            :columns="columns"
            :data="users"
            :total="total"
            :loading="loading"
            lazy
            :actions="tableActions"
            @refresh="load()"
            @query-change="onQueryChange"
          >
            <!-- Extra filters -->
            <template #toolbar-left>
              <Select
                v-model="roleFilter"
                :options="[{ label: t('admin.users.filterAllRoles'), value: null }, ...roleChoiceOptions]"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('admin.users.formRole')"
                class="w-10rem"
                :showClear="true"
                size="small"
              />
              <Select
                v-model="activeFilter"
                :options="[{ label: t('admin.users.filterAll'), value: null }, { label: t('admin.users.filterActive'), value: true }, { label: t('admin.users.filterInactive'), value: false }]"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('common.status')"
                class="w-9rem"
                :showClear="true"
                size="small"
              />
            </template>

            <template #toolbar>
              <pButton
                v-if="canCreate"
                :label="t('admin.users.addBtn')"
                icon="pi pi-plus"
                size="small"
                @click="openCreate"
              />
            </template>

            <!-- Name: first + last -->
            <template #cell-first_name="{ row }">
              {{ row.first_name }} {{ row.last_name }}
            </template>

            <!-- Company -->
            <template #cell-company_name="{ row }">
              <span v-if="row.company_name" class="company-badge">
                <i class="pi pi-building" style="font-size:.7rem;opacity:.6" />
                {{ row.company_name }}
              </span>
              <span v-else class="text-secondary" style="opacity:.4">—</span>
            </template>

            <!-- Role(s) — effektive RBAC-Rollen (user_roles), nicht die Legacy-Spalte -->
            <template #cell-role="{ row }">
              <span class="role-tags">
                <Tag
                  v-for="rn in effectiveRoles(row)"
                  :key="rn"
                  :value="rn"
                  :severity="roleSeverity(rn)"
                />
                <span v-if="!effectiveRoles(row).length" class="text-secondary" style="opacity:.4">—</span>
              </span>
            </template>

            <!-- Active -->
            <template #cell-is_active="{ row }">
              <Tag :value="row.is_active ? t('admin.users.statusActive') : t('admin.users.statusInactive')" :severity="row.is_active ? 'success' : 'secondary'" />
            </template>

            <template #cell-created_at="{ row }">{{ new Date(row.created_at).toLocaleString() }}</template>

          </AppTable>
        </template>
      </main>
    </div>

    <div class="layout-footer">
      <LayoutFooter />
    </div>

    <AppDialog
      v-model="showDialog"
      :title="isEdit ? t('admin.users.dialogEdit') : t('admin.users.dialogCreate')"
      size="lg"
      :showFooter="false"
      :maximizable="true"
    >
      <div class="grid formgrid p-fluid">
        <!-- Mode-Switch nur beim Create und nur für globale Admins
             (Company-Admins können ausschließlich einladen) -->
        <div v-if="!isEdit && isGlobalAdmin" class="col-12 mb-2">
          <label class="block mb-2">{{ t('admin.users.modeLabel') }}</label>
          <SelectButton
            v-model="createMode"
            :options="createModeOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            class="w-full mode-switch"
          >
            <template #option="{ option }">
              <span class="mode-option">
                <i :class="option.icon" />
                {{ option.label }}
              </span>
            </template>
          </SelectButton>
          <Message
            severity="info"
            :closable="false"
            class="mt-2"
            :icon="createMode === 'invite' ? 'pi pi-envelope' : 'pi pi-key'"
          >
            {{ createMode === 'invite' ? t('admin.users.modeInviteHint') : t('admin.users.modeManualHint') }}
          </Message>
        </div>

        <div class="col-12 md:col-6">
          <label>{{ t('admin.users.formEmail') }}</label>
          <InputText v-model="form.email" class="w-full" />
        </div>
        <div class="col-12 md:col-6">
          <label>{{ t('admin.users.formFirstName') }}</label>
          <InputText v-model="form.first_name" class="w-full" />
        </div>
        <div class="col-12 md:col-6">
          <label>{{ t('admin.users.formLastName') }}</label>
          <InputText v-model="form.last_name" class="w-full" />
        </div>
        <div class="col-12">
          <label>{{ t('admin.users.formAvatarUrl') }}</label>
          <InputText v-model="form.avatar_url" class="w-full" />
        </div>
        <div class="col-12 md:col-6">
          <label>{{ t('admin.users.formRole') }}</label>
          <Select v-model="form.role" :options="roleChoiceOptions" optionLabel="label" optionValue="value" :placeholder="t('admin.users.formRolePlaceholder')" class="w-full" />
        </div>
        <!-- Company-Auswahl nur für globale Admins; Company-Admins legen
             ausschließlich in der eigenen Company an (Backend erzwingt es). -->
        <div v-if="isGlobalAdmin" class="col-12 md:col-6">
          <label>{{ t('admin.users.formCompany') }}</label>
          <Select
            v-model="form.company_id"
            :options="companyOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('admin.users.formCompanyPlaceholder')"
            class="w-full"
            :showClear="!!form.company_id"
          />
        </div>
        <div class="col-12 md:col-6 flex align-items-end">
          <div class="flex align-items-center gap-2">
            <Checkbox v-model="form.is_active" :binary="true" inputId="active" />
            <label for="active">{{ t('admin.users.formActive') }}</label>
          </div>
        </div>
        <!-- Passwort-Feld nur bei Edit oder bei Create-Mode 'manual' -->
        <div v-if="isEdit || createMode === 'manual'" class="col-12">
          <label>{{ isEdit ? t('admin.users.formPasswordEdit') : t('admin.users.formPassword') }}</label>
          <InputText v-model="form.password" type="password" class="w-full" autocomplete="new-password" />
        </div>
        <div class="col-12 flex justify-end gap-2 mt-2">
          <pButton :label="t('common.cancel')" severity="secondary" @click="closeDialog" />
          <pButton
            :label="!isEdit && createMode === 'invite' ? t('admin.users.createAndInvite') : t('common.save')"
            :icon="!isEdit && createMode === 'invite' ? 'pi pi-send' : undefined"
            @click="save"
          />
        </div>
      </div>
    </AppDialog>
  </layoutShell>
</template>

<style scoped>
.mode-switch :deep(.p-togglebutton),
.mode-switch :deep(.p-selectbutton .p-button) { flex: 1; }
.mode-option {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  font-weight: 500;
}
.mode-option i { font-size: .9rem; }
.role-tags { display: inline-flex; flex-wrap: wrap; gap: .25rem; }
</style>
